import {
  Button,
  Icon,
  Input,
  Modal,
  PageHeader,
  Restricted,
  Select,
} from '@components';
import { TableFooter } from '@components/Table/TableFooter';
import { usePermissionContext } from '@contexts/PermissionProvider';
import { useModal } from '@hooks';
import { useConfirmationModal } from '@hooks/useConfirmationModal';
import BudgetExpenseLineAPICaller from '@services/api/budgetExpenseLine';
import CategoryAPICaller, { CategoryFilters } from '@services/api/category';
import CostCenterAPICaller from '@services/api/costCenter';
import {
  BudgetExpenseGridRow,
  BudgetExpenseRevision,
} from 'interfaces/BudgetExpenseLine';
import { Category } from 'interfaces/Category';
import { CostCenter } from 'interfaces/CostCenter';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import { formatCurrency } from 'utils/string/currency';
import './expenseGrid.css';

const MONTHS: { key: string; label: string }[] = [
  { key: 'january', label: 'Jan' },
  { key: 'february', label: 'Fev' },
  { key: 'march', label: 'Mar' },
  { key: 'april', label: 'Abr' },
  { key: 'may', label: 'Mai' },
  { key: 'june', label: 'Jun' },
  { key: 'july', label: 'Jul' },
  { key: 'august', label: 'Ago' },
  { key: 'september', label: 'Set' },
  { key: 'october', label: 'Out' },
  { key: 'november', label: 'Nov' },
  { key: 'december', label: 'Dez' },
];

const REVISION_OPTIONS: { label: string; value: BudgetExpenseRevision }[] = [
  { label: 'Anual', value: 'annual' },
  { label: 'Q1', value: 'q1' },
  { label: 'Q2', value: 'q2' },
  { label: 'Q3', value: 'q3' },
  { label: 'Q4', value: 'q4' },
];

const STATUS_OPTIONS: { label: string; value: '' | 'rascunho' | 'fechado' }[] = [
  { label: 'Todos', value: '' },
  { label: 'Em revisão', value: 'rascunho' },
  { label: 'Fechado', value: 'fechado' },
];

// Which months each revision actually owns — mirrors QUARTER_MONTHS on the
// API. A quarter revision only ever has these 3 months; the grid only shows
// the columns the currently selected revision owns (see visibleMonths).
const QUARTER_MONTHS: Record<'q1' | 'q2' | 'q3' | 'q4', string[]> = {
  q1: ['january', 'february', 'march'],
  q2: ['april', 'may', 'june'],
  q3: ['july', 'august', 'september'],
  q4: ['october', 'november', 'december'],
};

const monthsInRevision = (revision: BudgetExpenseRevision): string[] =>
  revision === 'annual' ? MONTHS.map((m) => m.key) : QUARTER_MONTHS[revision];

const centsToNumber = (cents?: number) => (Number(cents) || 0) / 100;
const floatToCents = (float: number) => Math.round(float * 100);
const centsToCurrency = (cents?: number) =>
  formatCurrency((Number(cents) || 0) / 100) ?? '';

const rowTotalCents = (row: BudgetExpenseGridRow, monthKeys: string[]) =>
  monthKeys.reduce((sum, month) => sum + (Number(row.months[month]) || 0), 0);

const HISTORY_START = 2025;
const PAGE_SIZE = 15;

export default function BudgetExpenseGridPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [revision, setRevision] = useState<BudgetExpenseRevision>('annual');
  const [statusFilter, setStatusFilter] = useState<'' | 'rascunho' | 'fechado'>(
    ''
  );
  const [rows, setRows] = useState<BudgetExpenseGridRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [costCenterFilter, setCostCenterFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handle);
  }, [search]);

  const { isOpen, toggle: toggleNewLineModal } = useModal();
  const confirmation = useConfirmationModal();
  const { hasPermission } = usePermissionContext();
  const canUpdate = hasPermission('budget_expense_lines', 'update');
  const {
    watch,
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const loadMatrix = useCallback(() => {
    setIsLoading(true);
    BudgetExpenseLineAPICaller.matrix({
      year,
      revision,
      status: statusFilter || undefined,
      costCenterId: costCenterFilter || undefined,
      search: debouncedSearch.trim() || undefined,
      page,
      size: PAGE_SIZE,
    })
      .then(({ rows: pageRows, total: matchingTotal }) => {
        setRows(pageRows);
        setTotal(matchingTotal);
      })
      .catch((error) => {
        // Without this the previous year's rows stay on screen as if they
        // were the new result — a 403 or 5xx would look like data.
        setRows([]);
        setTotal(0);
        const message =
          error?.response?.data?.message ||
          'Erro ao carregar as linhas de despesa.';
        toast.error(message);
      })
      .finally(() => setIsLoading(false));
  }, [year, revision, statusFilter, costCenterFilter, debouncedSearch, page]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  useEffect(() => {
    CostCenterAPICaller.listAll().then((values) =>
      setCostCenters(values.result || [])
    );
  }, []);

  const loadCategories = async (filters: CategoryFilters) => {
    await CategoryAPICaller.listAll(filters).then((values) =>
      setCategories(values.result)
    );
  };

  const pendingRef = useRef<Record<string, number>>({});
  const dirtyRef = useRef<Set<string>>(new Set());

  const isEditable = (row: BudgetExpenseGridRow, month: string) => {
    if (!canUpdate) return false;
    if (row.isDerived) return false;
    if (!monthsInRevision(row.revision).includes(month)) return false;
    return row.status === 'rascunho' || row.revision !== 'annual';
  };

  const setCell = (row: BudgetExpenseGridRow, month: string, cents: number) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id
          ? { ...r, months: { ...r.months, [month]: cents } }
          : r
      )
    );
    BudgetExpenseLineAPICaller.upsertCell({
      lineId: row.id,
      year,
      revision: row.revision,
      month,
      valueCents: cents,
    }).catch((error) => {
      const message = error?.response?.data?.message || 'Erro ao salvar valor.';
      toast.error(message);
      loadMatrix();
    });
  };

  const applyForward = (
    row: BudgetExpenseGridRow,
    month: string,
    cents: number
  ) => {
    BudgetExpenseLineAPICaller.applyFromMonth({
      lineId: row.id,
      year,
      revision: row.revision,
      month,
      valueCents: cents,
    })
      .then(() => {
        toast.success('Valor aplicado deste mês em diante.');
        loadMatrix();
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || 'Erro ao aplicar valor.';
        toast.error(message);
      });
  };

  const handleClose = (row: BudgetExpenseGridRow) => {
    confirmation({
      title: 'Fechar revisão anual',
      confirmButtonTitle: 'Fechar revisão',
      description:
        'Após fechar, o valor anual fica travado e os trimestres (Q1-Q4) ' +
        'passam a existir de forma independente. Essa ação não pode ser desfeita.',
      onSubmit: () =>
        BudgetExpenseLineAPICaller.closeRevision({ lineId: row.id, year })
          .then(() => {
            toast.success('Revisão fechada. Trimestres criados.');
            loadMatrix();
          })
          .catch(() => toast.error('Erro ao fechar revisão.')),
    });
  };

  const handleDelete = (row: BudgetExpenseGridRow) => {
    const closedWarning =
      row.status === 'fechado'
        ? ' Como está fechada, os espelhos no Consolidado (Anual e Q1-Q4) ' +
          'também serão removidos.'
        : '';
    confirmation({
      title: 'Excluir linha de despesa',
      confirmButtonTitle: 'Excluir',
      description:
        `Excluir "${row.categoryName} — ${row.customer}" de ${year}? ` +
        `Todos os valores mensais desse ano serão apagados.${closedWarning} ` +
        'Essa ação não pode ser desfeita.',
      onSubmit: () =>
        BudgetExpenseLineAPICaller.deleteLine({ lineId: row.id, year })
          .then(() => {
            toast.success('Linha excluída.');
            loadMatrix();
          })
          .catch(() => toast.error('Erro ao excluir linha.')),
    });
  };

  const handleRecreatePreviousYear = () => {
    confirmation({
      title: 'Recriar despesas do ano anterior',
      confirmButtonTitle: 'Recriar',
      description:
        `Criar as linhas de despesa de ${year - 1} novamente em ${year}, ` +
        'em revisão, com os valores anuais copiados. Você poderá ajustar antes ' +
        'de fechar.',
      onSubmit: () =>
        BudgetExpenseLineAPICaller.recreateFromPreviousYear(year)
          .then((result) => {
            if (result.created > 0) {
              toast.success(`${result.created} linha(s) recriada(s) em revisão.`);
            } else {
              toast.info(`Nenhuma linha encontrada em ${year - 1}.`);
            }
            loadMatrix();
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || 'Erro ao recriar linhas.';
            toast.error(message);
          }),
    });
  };

  const submitCreateLine = (data: any, supersedeLegacy?: boolean) => {
    return BudgetExpenseLineAPICaller.createLine({
      categoryId: data.categoryId,
      customer: data.customer,
      name: data.name,
      obs: data.obs,
      year,
      supersedeLegacy,
    })
      .then(() => {
        toast.success('Linha de despesa criada.');
        toggleNewLineModal();
        reset();
        loadMatrix();
      })
      .catch((error) => {
        if (error?.response?.data?.code === 'LEGACY_BUDGET_EXISTS') {
          confirmation({
            title: 'Budget manual já existe',
            confirmButtonTitle: 'Substituir e criar linha',
            description:
              'Já existe um budget manual (modelo antigo) pra essa categoria/fornecedor/descrição nesse ano. ' +
              'Criar a linha no grid vai apagar o budget manual antigo pra evitar contagem duplicada no ' +
              'Consolidado. Essa ação não pode ser desfeita.',
            onSubmit: () => submitCreateLine(data, true),
          });
          return;
        }
        const message =
          error?.response?.data?.message || 'Erro ao criar linha de despesa.';
        toast.error(message);
      });
  };

  const handleCreateLine = handleSubmit((data) => submitCreateLine(data));

  const yearOptions = Array.from(
    { length: currentYear + 1 - HISTORY_START + 1 },
    (_, i) => HISTORY_START + i
  ).map((y) => ({ label: String(y), value: String(y) }));

  const costCenterOptions = [
    { label: 'Todos os centros de custo', value: '' },
    ...costCenters.map((c) => ({ label: c.name, value: c.id as string })),
  ];

  // Only the months the selected filter actually owns — Q1-Q4 show just
  // their 3 months, Anual shows all 12. Cleaner than showing every column
  // with 9 of them blocked out.
  const visibleMonthKeys = monthsInRevision(revision);
  const visibleMonths = MONTHS.filter((m) =>
    visibleMonthKeys.includes(m.key)
  );

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const columnCount =
    (revision === 'annual' ? 4 : 2) + visibleMonths.length + 1;

  // The year has no expense lines at all — offer to recreate last year's.
  // Judged from the unfiltered annual view (no revision/status/search narrowing).
  const yearIsEmpty =
    !isLoading &&
    revision === 'annual' &&
    !statusFilter &&
    !costCenterFilter &&
    !search.trim() &&
    total === 0;

  return (
    <div className="p-s-300">
      <PageHeader title="Budget - despesas" />

      <p className="expense-intro">
        Planeje as despesas do ano em uma grade mensal por categoria. Ajuste os
        valores enquanto a linha estiver <strong>Em revisão</strong> e feche a
        revisão para congelar o anual e gerar os trimestres (Q1–Q4).
      </p>

      <div
        className="expense-grid-filters d-flex mt-s-200"
        style={{ gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <div style={{ minWidth: 220 }}>
          <div className="form-input__container">
            <label className="form-input__label">Buscar</label>
            <div className="form-input__addons__container">
              <input
                className="form-input"
                placeholder="Categoria, fornecedor ou descrição"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div style={{ minWidth: 200 }}>
          <Select
            label="Centro de custo"
            value={
              costCenterOptions.find((o) => o.value === costCenterFilter) ||
              costCenterOptions[0]
            }
            onSelect={(option: { label: string; value: string }) => {
              setCostCenterFilter(option.value);
              setPage(1);
            }}
            options={costCenterOptions}
          />
        </div>
        <div style={{ minWidth: 140 }}>
          <Select
            label="Ano"
            value={{ label: String(year), value: String(year) }}
            onSelect={(option: { label: string; value: string }) => {
              setYear(Number(option.value));
              setPage(1);
            }}
            options={yearOptions}
          />
        </div>
        <div style={{ minWidth: 160 }}>
          <Select
            label="Revisão"
            value={REVISION_OPTIONS.find((o) => o.value === revision)}
            onSelect={(option: { label: string; value: BudgetExpenseRevision }) => {
              setRevision(option.value);
              setPage(1);
            }}
            options={REVISION_OPTIONS}
          />
        </div>
        {revision === 'annual' && (
          <div style={{ minWidth: 160 }}>
            <Select
              label="Status"
              value={STATUS_OPTIONS.find((o) => o.value === statusFilter)}
              onSelect={(option: {
                label: string;
                value: '' | 'rascunho' | 'fechado';
              }) => {
                setStatusFilter(option.value);
                setPage(1);
              }}
              options={STATUS_OPTIONS}
            />
          </div>
        )}
        <Restricted context="budget_expense_lines" resource="create">
          <>
            {yearIsEmpty && (
              <Button
                design="outlined"
                suffixes={<Icon>content_copy</Icon>}
                onClick={handleRecreatePreviousYear}
              >
                Recriar despesas de {year - 1}
              </Button>
            )}
            <Button
              design="filled"
              suffixes={<Icon>add</Icon>}
              onClick={async () => {
                reset();
                setValue('costCenterId', undefined);
                setValue('categoryId', undefined);
                setCategories([]);
                toggleNewLineModal();
              }}
            >
              Nova linha de despesa
            </Button>
          </>
        </Restricted>
      </div>

      <section className="expense-section">
        <div className="expense-section__head">
          <div>
            <h3 className="expense-section__title">Grade mensal</h3>
            <p className="expense-section__caption">
              Uma linha por categoria/fornecedor. Edite os valores mês a mês;
              feche a revisão para gerar os trimestres.
            </p>
          </div>
          <span className="expense-section__meta">
            {revision === 'annual'
              ? 'Anual'
              : `Trimestre ${revision.toUpperCase()}`}{' '}
            · {total} linha{total === 1 ? '' : 's'}
          </span>
        </div>
        <div className="expense-grid-wrapper">
          {isLoading && rows.length === 0 ? (
            <p className="p-s-200">Carregando...</p>
          ) : (
            <table className="expense-grid-table">
              <thead>
                <tr>
                  <th className="sticky-col col-name" rowSpan={2}>
                    Categoria / Fornecedor
                  </th>
                <th rowSpan={2}>Item</th>
                {revision === 'annual' && <th rowSpan={2}>Status</th>}
                {visibleMonths.map((m) => (
                  <th key={m.key} rowSpan={2}>
                    {m.label}
                  </th>
                ))}
                <th rowSpan={2}>
                  {revision === 'annual' ? 'Total' : 'Total do trimestre'}
                </th>
                {revision === 'annual' && <th rowSpan={2}>Ação</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="empty">
                    Nenhuma linha de despesa encontrada para {year}.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                return (
                  <tr key={row.id}>
                    <td className="sticky-col col-name">
                      <div>
                        <strong>{row.categoryName}</strong>
                        {row.isDerived && (
                          <span
                            className="derived-badge"
                            title="Calculado automaticamente pela estimativa de colaboradores"
                          >
                            Automático
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>
                        {row.costCenterName} — {row.customer}
                      </div>
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {row.name}
                        {row.obs && (
                          <span
                            className="obs-tooltip-icon obs-tooltip"
                            data-tooltip={row.obs}
                            aria-label={row.obs}
                          >
                            <Icon>help</Icon>
                          </span>
                        )}
                      </div>
                    </td>
                    {revision === 'annual' && (
                      <td style={{ textAlign: 'center' }}>
                        <span
                          className={`status-badge status-badge--${row.status}`}
                        >
                          {row.status === 'fechado' ? 'Fechado' : 'Em revisão'}
                        </span>
                      </td>
                    )}
                    {visibleMonths.map((m) => {
                      const editable = isEditable(row, m.key);
                      const cents = row.months[m.key] ?? 0;
                      const cellKey = `${row.id}-${row.revision}-${m.key}`;
                      return (
                        <td key={m.key}>
                          <div className="cell-wrap">
                            <span className="currency-prefix">R$</span>
                            <NumericFormat
                              className="expense-grid-input"
                              key={`${cellKey}-${cents}`}
                              value={centsToNumber(cents)}
                              thousandSeparator="."
                              decimalSeparator=","
                              decimalScale={2}
                              fixedDecimalScale
                              disabled={!editable}
                              onValueChange={({ floatValue }) => {
                                if (!editable) return;
                                pendingRef.current[cellKey] = floatToCents(
                                  floatValue ?? 0
                                );
                                dirtyRef.current.add(cellKey);
                              }}
                              onBlur={() => {
                                if (!editable) return;
                                if (!dirtyRef.current.has(cellKey)) return;
                                dirtyRef.current.delete(cellKey);
                                setCell(
                                  row,
                                  m.key,
                                  pendingRef.current[cellKey] ?? cents
                                );
                              }}
                            />
                            <button
                              type="button"
                              className="apply-forward"
                              title="Aplicar deste mês em diante"
                              style={{
                                visibility: editable ? 'visible' : 'hidden',
                              }}
                              tabIndex={editable ? 0 : -1}
                              onMouseDown={() =>
                                dirtyRef.current.delete(cellKey)
                              }
                              onClick={() =>
                                editable &&
                                applyForward(
                                  row,
                                  m.key,
                                  pendingRef.current[cellKey] ?? cents
                                )
                              }
                            >
                              <Icon>double_arrow</Icon>
                            </button>
                          </div>
                        </td>
                      );
                    })}
                    <td className="total-cell">
                      {centsToCurrency(rowTotalCents(row, visibleMonthKeys))}
                    </td>
                    {revision === 'annual' && (
                      <td style={{ textAlign: 'center' }}>
                        <div
                          style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {row.status === 'rascunho' && (
                            <Restricted context="budget_expense_lines" resource="update">
                              <Button
                                design="transparent"
                                prefixes={<Icon>lock</Icon>}
                                title="Fechar revisão"
                                onClick={() => handleClose(row)}
                              />
                            </Restricted>
                          )}
                          <Restricted context="budget_expense_lines" resource="delete">
                            <Button
                              design="transparent"
                              prefixes={<Icon>delete</Icon>}
                              title="Excluir linha"
                              onClick={() => handleDelete(row)}
                            />
                          </Restricted>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {pageCount > 1 && (
              <TableFooter
                currentPage={page}
                pageCount={pageCount}
                setCurrentPage={setPage}
                onPageChange={(newPage) => setPage(newPage)}
                colSpan={columnCount}
              />
            )}
          </table>
        )}
        </div>
      </section>

      <Modal
        isOpen={isOpen}
        onClickAway={toggleNewLineModal}
        size="medium"
        title="Nova linha de despesa"
      >
        <form onSubmit={handleCreateLine}>
          <div className="row gap-x-2 mb-s-200">
            <div className="col-sm-6">
              <Select
                label="Centro de custo"
                value={watch('costCenterName')}
                placeholder="Selecione um centro de custo"
                form={register('costCenterId', { required: 'Obrigatório' })}
                onSelect={async (costCenter: any) => {
                  setValue('costCenterId', costCenter.value);
                  setValue('costCenterName', costCenter.label);
                  setValue('categoryId', '');
                  setValue('categoryName', undefined);
                  await loadCategories({ costCenterId: costCenter.value });
                }}
                options={costCenters.map((c) => ({
                  label: c.name,
                  value: c.id,
                }))}
              />
            </div>
            <div className="col-sm-6">
              <Select
                label="Categoria"
                value={watch('categoryName')}
                error={!!errors.categoryId}
                caption={errors.categoryId?.message as string}
                placeholder="Selecione uma categoria"
                form={register('categoryId', { required: 'Obrigatório' })}
                onSelect={(category: any) => {
                  setValue('categoryId', category.value);
                  setValue('categoryName', category.label);
                }}
                options={categories.map((c) => ({
                  label: c.name,
                  value: c.id,
                }))}
                disabled={!watch('costCenterId')}
              />
            </div>
          </div>
          <div className="row gap-x-2 mb-s-200">
            <div className="col-sm-6">
              <Input
                label="Fornecedor"
                error={!!errors.customer}
                caption={errors.customer?.message as string}
                placeholder="Digite um fornecedor"
                form={register('customer', { required: 'Obrigatório' })}
              />
            </div>
            <div className="col-sm-6">
              <Input
                label="Item"
                error={!!errors.name}
                caption={errors.name?.message as string}
                placeholder="Item"
                form={register('name', { required: 'Obrigatório' })}
              />
            </div>
          </div>
          <div className="row gap-x-2 mb-s-200">
            <div className="col-sm-12">
              <Input
                label="Observação"
                error={!!errors.obs}
                caption={errors.obs?.message as string}
                placeholder="Observação"
                form={register('obs')}
              />
            </div>
          </div>
          <div className="d-flex justify-end">
            <Button className="pl-s-900 pr-s-900" type="submit">
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
